import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './ManageClientContactsModal.module.css';

const ManageClientContactsModal = ({ clientId, contacts, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const validContacts = useMemo(
    () => contacts.filter((c) => c.user),
    [contacts]
  );

  const allSelected =
    validContacts.length > 0 && selectedIds.length === validContacts.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(validContacts.map((c) => c.id));
    }
  };

  const handleRemoveContact = async (contactId) => {
    if (
      !window.confirm(
        'Are you sure you want to remove this contact? This will also delete the associated user account.'
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/clients/contacts/${contactId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        alert('Contact successfully deleted');
        setSelectedIds((prev) => prev.filter((id) => id !== contactId));
        onSave();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to delete contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    if (
      !window.confirm(
        `Are you sure you want to remove ${selectedIds.length} contact${
          selectedIds.length === 1 ? '' : 's'
        }? This will also delete their associated user accounts.`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/clients/contacts/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ contactIds: selectedIds }),
      });

      const data = await response.json();

      if (response.ok) {
        const deletedCount = (data.deleted || []).length;
        const failedCount = (data.failed || []).length;
        const notFoundCount = (data.notFound || []).length;

        let message = `Deleted ${deletedCount} contact${deletedCount === 1 ? '' : 's'}.`;
        if (notFoundCount > 0) message += ` ${notFoundCount} not found.`;
        if (failedCount > 0) message += ` ${failedCount} failed.`;

        alert(message);
        setSelectedIds([]);
        onSave();
      } else {
        alert(data.message || 'Failed to delete contacts');
      }
    } catch (error) {
      console.error('Error bulk deleting contacts:', error);
      alert('Failed to delete contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            &times;
          </button>
          <h4 className={styles.modalTitle}>Manage contacts</h4>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.bulkBar}>
            <span className={styles.bulkInfo}>
              {selectedIds.length > 0
                ? `${selectedIds.length} selected`
                : `${validContacts.length} contact${validContacts.length === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              className={styles.btnDanger}
              onClick={handleBulkDelete}
              disabled={loading || selectedIds.length === 0}
            >
              Delete Selected
            </button>
          </div>

          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkboxCol}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                    disabled={validContacts.length === 0}
                    aria-label="Select all contacts"
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Job title</th>
                <th>Phone number</th>
                <th>Department</th>
                <th>Main Contact</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {validContacts.length > 0 ? (
                validContacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className={selectedIds.includes(contact.id) ? styles.rowSelected : ''}
                  >
                    <td className={styles.checkboxCol}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(contact.id)}
                        onChange={() => toggleOne(contact.id)}
                        aria-label={`Select ${contact.user.first_name} ${contact.user.last_name}`}
                      />
                    </td>
                    <td>{`${contact.user.first_name} ${contact.user.last_name}`}</td>
                    <td>{contact.user.email || '-'}</td>
                    <td>{contact.user.job_title || '-'}</td>
                    <td>
                      {contact.user.phone_number || contact.user.mobile_number || '-'}
                    </td>
                    <td>{contact.department || '-'}</td>
                    <td>
                      {contact.main_contact ? (
                        <span className={styles.badgeSuccess}>Yes</span>
                      ) : (
                        <span className={styles.badgeDefault}>No</span>
                      )}
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <Link
                          to={`/users/${contact.user.id}/edit`}
                          className={styles.btnInfo}
                        >
                          Edit
                        </Link>
                        <button
                          className={styles.btnDanger}
                          onClick={() => handleRemoveContact(contact.id)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className={styles.noData}>
                    No contacts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnDefault} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageClientContactsModal;
